import { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { Scale, Building2, Filter } from "lucide-react";
import SEO from "../components/SEO";

const StatusBadge = ({ status }) => {
  const s = status?.toLowerCase();
  const colors =
    s === "disposed"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : s === "pending"
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-slate-50 text-slate-600 border-slate-200";
  return (
    <span
      className={`inline-block text-[10px] tracking-[0.18em] uppercase px-2.5 py-1 border rounded-full font-semibold ${colors}`}
    >
      {status}
    </span>
  );
};

export default function CaseExperience() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");

  useEffect(() => {
    api
      .get("/cases")
      .then((r) => setCases(r.data))
      .catch(() => setCases([]))
      .finally(() => setLoading(false));
  }, []);

  const caseTypes = useMemo(
    () => ["All", ...Array.from(new Set(cases.map((c) => c.case_type)))],
    [cases]
  );
  const statuses = useMemo(
    () => ["All", ...Array.from(new Set(cases.map((c) => c.status)))],
    [cases]
  );

  const filtered = cases.filter(
    (c) =>
      (filterType === "All" || c.case_type === filterType) &&
      (filterStatus === "All" || c.status === filterStatus)
  );

  return (
    <div className="pt-[72px]" data-testid="case-experience-page">
      <SEO
        title="Case Experience — Advocate Shubham Makkar (PHHC & Chandigarh Courts)"
        description="Selected matters represented by Advocate Shubham Makkar before Punjab &amp; Haryana High Court, Chief Judicial Magistrate Chandigarh and District Courts."
        keywords="corporate lawyer case record chandigarh, phhc bail cases, advocate shubham makkar cases"
      />

      <section className="relative bg-gradient-to-b from-[#EFF6FF] to-white overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-16">
          <div className="inline-flex items-center gap-2 bg-white border border-blue-100 shadow-sm px-4 py-2 rounded-full mb-6">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
            <span className="text-xs tracking-wider text-[#1D4ED8] font-medium">
              Public Record · Verified
            </span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0A2540] tracking-tight leading-[1.05] max-w-4xl">
            Case experience — <em className="text-[#1D4ED8] not-italic">argued, not advertised</em>.
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-3xl leading-relaxed">
            A curated view of matters represented by Advocate Shubham Makkar before PHHC,
            Sessions Courts and Chief Judicial Magistrate courts. Sensitive details are omitted
            in line with bar council confidentiality norms.
          </p>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-white py-5 sticky top-[72px] z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-wrap items-center gap-3">
          <Filter size={16} className="text-[#1D4ED8]" />
          <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-500">Filters</span>
          <div className="flex flex-wrap gap-2" data-testid="filter-type">
            {caseTypes.map((t) => (
              <button
                key={t}
                onClick={() => setFilterType(t)}
                className={`text-xs px-3.5 py-1.5 border rounded-full transition-all font-medium ${filterType === t
                    ? "bg-[#1D4ED8] text-white border-[#1D4ED8] shadow-md"
                    : "bg-white text-slate-600 border-slate-200 hover:border-[#1D4ED8] hover:text-[#1D4ED8]"
                  }`}
                data-testid={`filter-type-${t.replace(/\s+/g, "-")}`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 ml-auto" data-testid="filter-status">
            {statuses.map((s) => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`text-xs px-3.5 py-1.5 border rounded-full transition-all font-medium ${filterStatus === s
                    ? "bg-[#1D4ED8] text-white border-[#1D4ED8] shadow-md"
                    : "bg-white text-slate-600 border-slate-200 hover:border-[#1D4ED8] hover:text-[#1D4ED8]"
                  }`}
                data-testid={`filter-status-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {loading && <div className="text-slate-500">Loading cases…</div>}
          {!loading && filtered.length === 0 && (
            <div className="text-slate-500 py-12 text-center">No cases match the current filter.</div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filtered.map((c) => (
              <article
                key={c.id}
                className="card-elevated p-8"
                data-testid={`case-card-${c.id}`}
              >
                <div className="flex items-start gap-3 mb-5">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#EFF6FF] to-[#DBEAFE] flex items-center justify-center rounded-lg flex-shrink-0">
                    <Scale size={18} className="text-[#1D4ED8]" />
                  </div>
                  <h3 className="font-serif text-xl text-[#0A2540] leading-snug flex-1 font-semibold pt-0.5">{c.title}</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-[10px] tracking-[0.18em] uppercase text-slate-400 mb-1 font-semibold">
                      Court
                    </div>
                    <div className="text-slate-700 flex items-center gap-2">
                      <Building2 size={14} className="text-[#1D4ED8]" /> {c.court}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-[0.18em] uppercase text-slate-400 mb-1 font-semibold">
                      Case Type
                    </div>
                    <div className="text-slate-700">{c.case_type}</div>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-[0.18em] uppercase text-slate-400 mb-1 font-semibold">
                      Status
                    </div>
                    <StatusBadge status={c.status} />
                  </div>
                  {c.case_category && (
                    <div>
                      <div className="text-[10px] tracking-[0.18em] uppercase text-slate-400 mb-1 font-semibold">
                        Category
                      </div>
                      <div className="text-slate-700">{c.case_category}</div>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
